import { KompisElement, property } from '@inter-ikea-kompis/web-component';
import { FontStyle } from '@inter-ikea-kompis/themes';
import type { IDexfSettings, ILocalizedInformation, ITheme } from '@inter-ikea-kompis/types';

import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { ShareDesignCard } from '@inter-ikea-kompis/component-share-design-card';
import {
	ShareDesignCardCopyStateEnum,
	ShareDesignCardStateEnum
} from '@inter-ikea-kompis/component-share-design-card';

import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import {
	ModalFooter as SkapaModalFooter,
	ModalHeader as SkapaModalHeader,
	Prompt as SkapaPrompt,
	Sheet as SkapaSheet
} from '@ingka/modal-webc/passive.js';

import ConfigurationSummaryModalEnum from '../../enums/ConfigurationSummaryModalEnum.js';
import type IConfigurationSummaryAnalyticsProp from '../../types/IConfigurationSummaryAnalyticsProp.js';
import type IConfigurationSummaryShareDesignCardProps from '../../types/IConfigurationSummaryShareDesignCardProps.js';
import type IConfigurationSummaryTranslations from '../../types/IConfigurationSummaryTranslations.js';

declare global {
	interface HTMLElementTagNameMap {
		'skapa-sheet': SkapaSheet;
		'skapa-modal-footer': SkapaModalFooter;
		'skapa-modal-header': SkapaModalHeader;
		'skapa-prompt': SkapaPrompt;
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-icon': SkapaIcon;
	}
}

/**
 * A sheet containing ShareDesignCard.
 *
 * @event modalClose Triggered when the user clicks on the sheet close button.
 * @event shareDesignCardStateChange Triggered when the state for ShareDesignCard changes.
 * @event sendByEmailInput Triggered when the user writes in the sendByEmail view.
 * @event sendByEmailValidation Triggered when the user tries to send with an invalid email.
 * @event sendByEmailSend Triggered when the user clicks send in the sendByEmail view.
 * @event sendByEmailReset Triggered 3 seconds after SendByEmailState is set to success.
 * @event copyDesignCodeClick Triggered when the user clicks the copy design code button.
 * @event copyDesignCodeReset Triggered 3 seconds after copyDesignCodeState is set to success.
 * @event copyDesignLinkClick Triggered when the user clicks the copy design link button.
 * @event copyDesignLinkReset Triggered 3 seconds after copyDesignLinkState is set to success.
 */
export default class ConfigurationSummaryShareDesignSheet extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IConfigurationSummaryTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IDexfSettings | null = null;

	// Locale, e.g. en-GB.
	@property({ type: String, required: true })
	public locale!: string;

	// VPC code for the solution.
	@property({ type: String })
	public vpcCode: string | null = null;

	// Disables outgoing links.
	@property({ type: Boolean })
	public disableLinks = false;

	// Visible modal
	@property({ type: Object })
	public visibleModal: ConfigurationSummaryModalEnum | null = null;

	// A collection of props used for the ShareDesignCard component rendered in the sheet.
	@property({ type: Object })
	public shareDesignCard: IConfigurationSummaryShareDesignCardProps | null = null;

	// The url for the screenshot.
	@property({ type: String })
	public screenshotUrl: string | null = null;

	// The alt text for the screenshot.
	@property({ type: String })
	public screenshotAlt: string | null = null;

	// The name of the product, e.g. PLATSA.
	@property({ type: String })
	public productName: string | null = null;

	// Description of the product, e.g. "Wardrobe, 200x300x55 cm".
	@property({ type: String })
	public productDescription: string | null = null;

	// Used for automatic analytics. If null or not set, analytics is turned off.
	@property({ type: Object })
	public analytics: IConfigurationSummaryAnalyticsProp | null = null;

	private shareDesignCardSheet: SkapaSheet | null = null;
	private backButton: SkapaIconButton | null = null;
	public override render(): null {
		return null;
	}

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-sheet', SkapaSheet);
		this.customElements.define('skapa-modal-footer', SkapaModalFooter);
		this.customElements.define('skapa-modal-header', SkapaModalHeader);
		this.customElements.define('skapa-prompt', SkapaPrompt);
		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('kompis-share-design-card', ShareDesignCard);

		super.connectedCallback();
	}

	protected override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.closeSheet();
	}

	/**
	 * Since this component uses pure javascript to render, it also means it must update props by itself.
	 * Listening to changes using the shouldUpdate method and then updating props makes it so that this
	 * component is updated when props are updated.
	 *
	 */
	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('visibleModal')) {
			if (this.visibleModal === ConfigurationSummaryModalEnum.shareDesignCard) {
				this.openSheet();
			} else {
				this.closeSheet();
			}
		}

		if (
			changedProperties.has('shareDesignCard') ||
			changedProperties.has('screenshotAlt') ||
			changedProperties.has('screenshotUrl') ||
			changedProperties.has('productName') ||
			changedProperties.has('productDescription') ||
			changedProperties.has('analytics')
		) {
			this.updateSheetElements();
		}

		return true;
	}

	private getTranslations(): IConfigurationSummaryTranslations {
		const translations = this.translations
			? this.translations
			: this.localizedInformation?.translations;

		if (!translations) {
			throw new Error(
				'Translations must be defined either through the "translations" or "localizedInformation" prop.'
			);
		}

		return translations;
	}

	private closeSheet(): void {
		if (this.shareDesignCardSheet) {
			this.shareDesignCardSheet.open = false;
			this.shareDesignCardSheet = null;
			this.backButton = null;
		}
	}

	private openSheet(): void {
		// Sheet is already shown
		if (this.shareDesignCardSheet) {
			return;
		}
		// Sheet should not be shown
		if (!this.vpcCode || !this.shareDesignCard) {
			return;
		}

		const shareDesignCardSheet = this.createShareDesignSheet();
		this.shareDesignCardSheet = shareDesignCardSheet;

		const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
		parent.appendChild(shareDesignCardSheet);

		shareDesignCardSheet.open = true;
	}

	private updateSheetElements(): void {
		if (this.shareDesignCardSheet) this.setPropsOnSheet(this.shareDesignCardSheet);

		const sheetHeader = this.shareDesignCardSheet?.querySelector(
			this.customElements.getInternalName('skapa-modal-header')
		);
		if (sheetHeader) this.toggleSheetHeaderBackButton(sheetHeader);

		const card = this.shareDesignCardSheet?.querySelector(
			this.customElements.getInternalName('kompis-share-design-card')
		);
		if (card) this.setPropsOnShareDesignCard(card);
	}

	private setPropsOnSheet(sheetElement: SkapaSheet): void {
		const {
			shareDesignCardDefaultTitle,
			shareDesignCardSendByEmailTitle,
			shareDesignCardGetDesignCodeTitle,
			shareDesignCardGetLinkTitle
		} = this.getTranslations();

		const ariaLabels = {
			[ShareDesignCardStateEnum.default]: shareDesignCardDefaultTitle,
			[ShareDesignCardStateEnum.sendByEmail]: shareDesignCardSendByEmailTitle,
			[ShareDesignCardStateEnum.getDesignCode]: shareDesignCardGetDesignCodeTitle,
			[ShareDesignCardStateEnum.getLink]: shareDesignCardGetLinkTitle
		};

		if (this.shareDesignCard?.state) {
			sheetElement.setAttribute('aria-label', ariaLabels[this.shareDesignCard?.state]);
		}
		sheetElement.size = 'small';
		sheetElement.fullHeight = true;
		sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
		sheetElement.style.zIndex = '500';
	}

	private setPropsOnShareDesignCard(cardElement: ShareDesignCard): void {
		if (!this.vpcCode || !this.shareDesignCard) {
			return;
		}

		cardElement.theme = this.theme;
		cardElement.localizedInformation = this.localizedInformation;
		cardElement.translations = this.translations;
		cardElement.settings = this.settings;
		cardElement.vpcCode = this.vpcCode;
		cardElement.locale = this.locale;
		cardElement.state = this.shareDesignCard.state;
		cardElement.disableLinks = this.disableLinks;
		cardElement.screenshotUrl = this.screenshotUrl;
		cardElement.screenshotAlt = this.screenshotAlt;
		if (this.productName) {
			cardElement.productName = this.productName;
		}
		if (this.productDescription) {
			cardElement.productDescription = this.productDescription;
		}
		if (this.shareDesignCard.autocomplete) {
			cardElement.autocomplete = this.shareDesignCard.autocomplete;
		}
		if (this.shareDesignCard.showGetLink) {
			cardElement.showGetLink = this.shareDesignCard.showGetLink;
		}
		if (this.shareDesignCard.showSendByEmail) {
			cardElement.showSendByEmail = this.shareDesignCard.showSendByEmail;
		}
		if (this.shareDesignCard.sendByEmailState) {
			cardElement.sendByEmailState = this.shareDesignCard.sendByEmailState;
		}
		if (this.shareDesignCard.showCopyDesignCodeButton) {
			cardElement.showCopyDesignCodeButton = this.shareDesignCard.showCopyDesignCodeButton;
		}
		if (this.shareDesignCard.showQrCode) {
			cardElement.showQrCode = this.shareDesignCard.showQrCode;
		}
		if (this.shareDesignCard.designLink) {
			cardElement.designLink = this.shareDesignCard.designLink;
		}
		cardElement.analytics = this.analytics;
	}

	private createShareDesignSheet(): SkapaSheet {
		const sheetElement = this.createSheet();
		const sheetHeaderElement = this.createSheetHeader();
		const shareDesignCardElement = this.createShareDesignCard();

		sheetElement.appendChild(sheetHeaderElement);
		sheetElement.appendChild(shareDesignCardElement);

		return sheetElement;
	}

	private createSheet(): SkapaSheet {
		const sheetElement = this.createInternalElement('skapa-sheet');
		this.setPropsOnSheet(sheetElement);
		sheetElement.addEventListener('closerequest', () => {
			if (this.shareDesignCardSheet) {
				this.dispatchEvent(
					new CustomEvent('modalClose', {
						bubbles: true,
						detail: {
							visibleModal: null
						}
					})
				);
			}
		});
		sheetElement.addEventListener('closed', () => {
			sheetElement.parentElement?.removeChild(sheetElement);
		});

		return sheetElement;
	}

	private createSheetHeader(): SkapaModalHeader {
		const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
		sheetHeaderElement.slot = 'header';

		const closeButtonLabelSlot = document.createElement('span');
		closeButtonLabelSlot.slot = 'closebutton-label';
		closeButtonLabelSlot.innerText = this.getTranslations().modalAriaLabelButtonClose;
		sheetHeaderElement.appendChild(closeButtonLabelSlot);

		this.toggleSheetHeaderBackButton(sheetHeaderElement);

		sheetHeaderElement.addEventListener('backrequest', () =>
			this.dispatchEvent(
				new CustomEvent('shareDesignCardStateChange', {
					bubbles: true,
					detail: { selectedState: ShareDesignCardStateEnum.default }
				})
			)
		);

		return sheetHeaderElement;
	}

	private toggleSheetHeaderBackButton(sheetHeaderElement: SkapaModalHeader): void {
		if (this.shareDesignCard?.state !== ShareDesignCardStateEnum.default) {
			if (!sheetHeaderElement.contains(this.backButton)) {
				const backButton = this.createBackButton();
				this.backButton = backButton;
				sheetHeaderElement.appendChild(backButton);
			}
		} else {
			if (this.backButton && sheetHeaderElement.contains(this.backButton)) {
				sheetHeaderElement.removeChild(this.backButton);
			}
			this.backButton = null;
		}
	}

	private createBackButton(): SkapaIconButton {
		const backButton = this.createInternalElement('skapa-icon-button');

		backButton.a11yLabel = this.getTranslations().modalAriaLabelButtonBack || '';

		backButton.variant = 'primary';
		backButton.inverse = true;
		backButton.size = 'small';
		backButton.slot = 'back-button';

		const icon = this.createInternalElement('skapa-icon');
		icon.icon = 'arrow-left';
		icon.flipRTL = true;
		icon.slot = 'icon';
		backButton.append(icon);

		return backButton;
	}

	private createShareDesignCard(): ShareDesignCard {
		const cardElement = this.createInternalElement('kompis-share-design-card');
		this.setPropsOnShareDesignCard(cardElement);

		// Casting to EventListener to be able to access detail, which only exists in CustomEvent
		cardElement.addEventListener('select', <EventListener>((event: CustomEvent) => {
			cardElement.copyDesignLinkState = ShareDesignCardCopyStateEnum.default;
			cardElement.copyDesignCodeState = ShareDesignCardCopyStateEnum.default;
			this.dispatchEvent(
				new CustomEvent('shareDesignCardStateChange', { bubbles: true, detail: event.detail })
			);
		}));
		cardElement.addEventListener('copyDesignCodeClick', <EventListener>((event: CustomEvent) => {
			cardElement.copyDesignCodeState = event.detail.state;
			this.dispatchEvent(
				new CustomEvent('copyDesignCodeClick', { bubbles: true, detail: event.detail })
			);
		}));
		cardElement.addEventListener('copyDesignCodeReset', () => {
			cardElement.copyDesignCodeState = ShareDesignCardCopyStateEnum.default;
			this.dispatchEvent(new CustomEvent('copyDesignCodeReset', { bubbles: true }));
		});
		cardElement.addEventListener('copyDesignLinkClick', <EventListener>((event: CustomEvent) => {
			cardElement.copyDesignLinkState = event.detail.state;
			this.dispatchEvent(
				new CustomEvent('copyDesignLinkClick', { bubbles: true, detail: event.detail })
			);
		}));
		cardElement.addEventListener('copyDesignLinkReset', () => {
			cardElement.copyDesignLinkState = ShareDesignCardCopyStateEnum.default;
			this.dispatchEvent(new CustomEvent('copyDesignLinkReset', { bubbles: true }));
		});
		cardElement.addEventListener('sendByEmailInput', <EventListener>((event: CustomEvent) => {
			cardElement.sendByEmailValue = event.detail.value;
			this.dispatchEvent(
				new CustomEvent('sendByEmailInput', { bubbles: true, detail: event.detail })
			);
		}));
		cardElement.addEventListener('sendByEmailValidation', <EventListener>((event: CustomEvent) => {
			cardElement.sendByEmailInvalidInput = event.detail.invalidInput;
			this.dispatchEvent(
				new CustomEvent('sendByEmailValidation', { bubbles: true, detail: event.detail })
			);
		}));
		cardElement.addEventListener('sendByEmailSend', <EventListener>((event: CustomEvent) => {
			this.dispatchEvent(
				new CustomEvent('sendByEmailSend', { bubbles: true, detail: event.detail })
			);
		}));
		cardElement.addEventListener('sendByEmailReset', <EventListener>((event: CustomEvent) => {
			cardElement.sendByEmailValue = '';
			this.dispatchEvent(
				new CustomEvent('sendByEmailReset', { bubbles: true, detail: event.detail })
			);
		}));

		return cardElement;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-configuration-summary-share-design-sheet': ConfigurationSummaryShareDesignSheet;
	}
}
